import { useState, useEffect, useMemo } from 'react';
import { adminLogin, isAuthenticated } from '../../services/apiService';

const T = {
  bg: '#F4F2EE', white: '#FFFFFF', border: '#E0DDD6', borderSm: '#ECEAE4',
  text: '#1C1A17', muted: '#7A7772', faint: '#B5B0AB',
  brand: '#3E4954', brandDark: '#293038',
  accent: '#FF4500', accentBg: 'rgba(255,69,0,0.08)',
  blue: '#2563EB', blueBg: '#EFF6FF',
  green: '#166534', greenBg: '#DCFCE7',
  amber: '#92400E', amberBg: '#FEF3C7',
  red: '#9F1239', redBg: '#FFE4E6',
  purple: '#5B21B6', purpleBg: '#EDE9FE',
};

const STATUS_STYLE = {
  'Findet statt': { bg: T.greenBg, color: T.green },
  'In Planung':   { bg: T.amberBg, color: T.amber },
  'Abgeschlossen':{ bg: '#F3F4F6', color: '#374151' },
};

const TYPE_STYLE = {
  'Messe':                 { bg: T.blueBg,   color: T.blue },
  'Trägerevent':           { bg: T.purpleBg, color: T.purple },
  'Einzelveranstaltung ZBA':{ bg: T.accentBg, color: T.accent },
  'Präsentation':          { bg: '#ECFDF5',  color: '#065F46' },
};

function Tag({ label, style }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100,
      textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
      ...style }}>
      {label}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  const date = new Date(`${y}-${m}-${day}T12:00:00`);
  const days = ['So','Mo','Di','Mi','Do','Fr','Sa'];
  return `${days[date.getDay()]}, ${day}.${m}.${y}`;
}

function daysFromNow(d) {
  const diff = Math.round((new Date(d + 'T12:00:00') - new Date()) / 86400000);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  if (diff === -1) return 'Gestern';
  if (diff > 0) return `in ${diff} Tagen`;
  return `vor ${Math.abs(diff)} Tagen`;
}

function StatBox({ label, value, sub, color }) {
  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || T.text, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.06em', marginTop: 6 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: T.faint, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function LeadCard({ lead }) {
  const [open, setOpen] = useState(false);
  const hasTer = lead.termin_date && lead.termin_time;
  return (
    <div style={{ background: T.white, border: `1px solid ${T.borderSm}`, borderRadius: 10,
      overflow: 'hidden', transition: 'box-shadow 120ms', borderLeft: `4px solid ${hasTer ? '#16a34a' : '#F59E0B'}` }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
        cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        {/* Avatar */}
        <div style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700,
          background: hasTer ? T.brand : '#F59E0B', color: 'white' }}>
          {(lead.vorname?.[0] || '?')}{(lead.nachname?.[0] || '')}
        </div>
        {/* Name + contact */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{lead.vorname} {lead.nachname}</div>
          <div style={{ fontSize: 12, color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' }}>{lead.email || lead.telefon}</div>
        </div>
        {/* Badges */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {hasTer ? <Tag label="TERMIN ✓" style={{ background: '#DCFCE7', color: '#166534' }} /> : <Tag label="📞 RÜCKRUF" style={{ background: '#FEF3C7', color: '#92400E' }} />}
          {lead.bildungsangebot ? <Tag label="BA" style={{ background: T.blueBg, color: T.blue }} /> : null}
          {lead.kurslaenge ? <Tag label={lead.kurslaenge} style={{ background: T.amberBg, color: T.amber }} /> : null}
        </div>
        <span style={{ color: T.faint, fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${T.borderSm}`,
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, paddingTop: 14 }}>
          {[
            ['Telefon', lead.telefon],
            ['E-Mail', lead.email],
            ['Sprachniveau', lead.sprachniveau],
            ['Führerschein', lead.fuehrerschein ? '✅ Ja' : '❌ Nein'],
            ['Auto', lead.auto ? '✅ Ja' : '❌ Nein'],
            ['Kundennummer', lead.kundennummer],
            ['Kurslänge', lead.kurslaenge],
            ['Termin', hasTer ? `${lead.termin_date} ${lead.termin_time}` : '📞 Kein Termin (Rückruf)'],
            ['Braucht Einstellungszusage', lead.braucht_einstellungszusage ? '⚠️ Ja' : null],
            ['Braucht Eignungsprüfung', lead.braucht_eignungspruefung ? '⚠️ Ja' : null],
            ['Braucht Sprachtest', lead.braucht_sprachtest ? '⚠️ Ja' : null],
            ['Unterschrift', lead.signature_given ? '✅ Gegeben' : '❌ Fehlt'],
            ['Einwilligung', lead.consent_timestamp ? new Date(lead.consent_timestamp).toLocaleString('de-DE') : null],
            ['Pipedrive', lead.pd_deal_id ? `#${lead.pd_deal_id}` : null],
            ['Erfasst', lead.created_at?.substring(0,16)?.replace('T',' ')],
          ].filter(([,v]) => v).map(([k, v]) => (
            <div key={k}>
              <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.05em', marginBottom: 3 }}>{k}</div>
              <div style={{ fontSize: 13, color: T.text }}>{v}</div>
            </div>
          ))}
          {lead.signature_data && (
            <div style={{ gridColumn: '1/-1', marginTop: 8 }}>
              <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.05em', marginBottom: 6 }}>Unterschrift</div>
              <img src={lead.signature_data} alt="Unterschrift"
                style={{ maxWidth: 280, height: 70, border: `1px solid ${T.border}`,
                  borderRadius: 8, background: 'white', display: 'block' }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EventCard({ event, onSelect, selected }) {
  const past = new Date(event.event_date) < new Date(new Date().setHours(0,0,0,0));
  const statusSt = STATUS_STYLE[event.status] || { bg: T.borderSm, color: T.muted };
  const typeSt = TYPE_STYLE[event.event_type] || { bg: T.borderSm, color: T.muted };

  return (
    <div onClick={() => onSelect(event)} style={{
      background: selected ? T.brandDark : T.white,
      color: selected ? 'white' : T.text,
      border: `2px solid ${selected ? T.brand : T.border}`,
      borderRadius: 14, padding: '16px 20px', cursor: 'pointer',
      transition: 'all 160ms', opacity: past ? 0.65 : 1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 5, lineHeight: 1.3 }}>{event.name}</div>
          {event.location && (
            <div style={{ fontSize: 12, color: selected ? 'rgba(255,255,255,0.6)' : T.muted, marginBottom: 8,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              📍 {event.location}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Tag label={event.status || 'Unbekannt'} style={selected ? { background: 'rgba(255,255,255,0.15)', color: 'white' } : statusSt} />
            {event.event_type && <Tag label={event.event_type} style={selected ? { background: 'rgba(255,255,255,0.12)', color: 'white' } : typeSt} />}
            {event.projekt_id && <Tag label={`🔗 ${event.projekt_id}`} style={selected ? { background: 'rgba(255,255,255,0.12)', color: 'white' } : { background: '#EDE9FE', color: '#5B21B6' }} />}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: selected ? 'rgba(255,255,255,0.9)' : T.text }}>
            {formatDate(event.event_date)}
          </div>
          <div style={{ fontSize: 11, color: selected ? 'rgba(255,255,255,0.5)' : T.faint, marginTop: 2 }}>
            {daysFromNow(event.event_date)}
          </div>
          {event.lead_count > 0 && (
            <div style={{ marginTop: 8, fontSize: 13, fontWeight: 800,
              color: selected ? 'white' : T.accent }}>
              {event.lead_count} Lead{event.lead_count !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewEventModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', location: '', event_date: '', event_type: 'Einzelveranstaltung ZBA', status: 'Findet statt', organisator: '', projekt_id: '' });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.name || !form.event_date) return;
    setLoading(true);
    const r = await fetch('/api/admin/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionStorage.getItem('zba_admin_token')}` },
      body: JSON.stringify(form),
    });
    const d = await r.json();
    if (d.success) onCreated();
    setLoading(false);
  };

  const inp = (k) => ({
    value: form[k], autoComplete: 'new-password',
    onChange: e => setForm(p => ({...p, [k]: e.target.value})),
    style: { width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${T.border}`,
      fontFamily: 'inherit', fontSize: 14, marginTop: 6, display: 'block', background: T.bg }
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
      <div style={{ background: T.white, borderRadius: 16, padding: 28, width: '100%', maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>Neues Event anlegen</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.muted }}>×</button>
        </div>
        {[['Name', 'name'], ['Adresse', 'location'], ['Organisator', 'organisator'], ['Projekt-ID', 'projekt_id']].map(([lbl, key]) => (
          <label key={key} style={{ display: 'block', marginBottom: 14, fontSize: 13, fontWeight: 600, color: T.muted }}>
            {lbl}<input {...inp(key)} placeholder={lbl} />
          </label>
        ))}
        <label style={{ display: 'block', marginBottom: 14, fontSize: 13, fontWeight: 600, color: T.muted }}>
          Datum<input type="date" {...inp('event_date')} />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.muted }}>
            Typ
            <select {...inp('event_type')} style={{...inp('event_type').style}}>
              {['Messe','Trägerevent','Einzelveranstaltung ZBA','Präsentation'].map(t => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.muted }}>
            Status
            <select {...inp('status')} style={{...inp('status').style}}>
              {['Findet statt','In Planung','Abgeschlossen'].map(s => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${T.border}`,
            background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Abbrechen</button>
          <button onClick={submit} disabled={loading} style={{ padding: '10px 20px', borderRadius: 8,
            background: T.brand, color: 'white', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            fontWeight: 600 }}>{loading ? '...' : 'Anlegen'}</button>
        </div>
      </div>
    </div>
  );
}

export default function EventsAdmin() {
  const [auth, setAuth] = useState(isAuthenticated());
  const [pw, setPw] = useState('');
  const [events, setEvents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  const loadEvents = async () => {
    setLoading(true);
    const r = await fetch('/api/admin/events', {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('zba_admin_token')}` }
    });
    const d = await r.json();
    setEvents(d.events || []);
    setLoading(false);
  };

  const loadLeads = async (eventId) => {
    setLoadingLeads(true);
    const r = await fetch(`/api/admin/events/${eventId}/leads`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('zba_admin_token')}` }
    });
    const d = await r.json();
    setLeads(d.leads || []);
    setLoadingLeads(false);
  };

  useEffect(() => { if (auth) loadEvents(); }, [auth]);
  useEffect(() => { if (selected) loadLeads(selected.id); else setLeads([]); }, [selected]);

  const handleLogin = async e => {
    e.preventDefault();
    const r = await adminLogin(pw);
    if (r.success) setAuth(true);
  };

  const filtered = useMemo(() => events.filter(ev => {
    const q = search.toLowerCase();
    const ms = !q || ev.name.toLowerCase().includes(q) || (ev.location || '').toLowerCase().includes(q);
    const mst = !filterStatus || ev.status === filterStatus;
    const mty = !filterType || ev.event_type === filterType;
    return ms && mst && mty;
  }), [events, search, filterStatus, filterType]);

  // Stats
  const totalLeads = events.reduce((s, e) => s + (e.lead_count || 0), 0);
  const withTermin = leads.filter(l => l.termin_date).length;
  const ohneTermin = leads.filter(l => !l.termin_date).length;
  const withBGS = leads.filter(l => l.bildungsangebot).length;

  if (!auth) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: T.bg, fontFamily: 'system-ui' }}>
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 36, width: 300 }}>
        <img src="https://zba.academy/webinar/images/zbalogo2.png" style={{ height: 24, marginBottom: 20 }} alt="ZBA" />
        <form onSubmit={handleLogin}>
          <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Passwort" autoFocus
            style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8,
              fontFamily: 'inherit', fontSize: 14, marginBottom: 12 }} />
          <button type="submit" style={{ width: '100%', padding: 11, background: T.brand, color: 'white',
            border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
            Einloggen
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: 'system-ui', fontSize: 14 }}>
      {showNew && <NewEventModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); loadEvents(); }} />}

      {/* Header */}
      <div style={{ background: T.white, borderBottom: `1px solid ${T.border}`, position: 'sticky',
        top: 0, zIndex: 20, height: 52, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="https://zba.academy/webinar/images/zbalogo2.png" style={{ height: 20 }} alt="ZBA" />
          <span style={{ width: 1, height: 18, background: T.borderSm }} />
          <span style={{ fontWeight: 700, fontSize: 14 }}>Events & Leads</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowNew(true)}
            style={{ height: 34, padding: '0 16px', background: T.brand, color: 'white', border: 'none',
              borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
            + Event
          </button>
          <a href="/events" target="_blank"
            style={{ height: 34, padding: '0 14px', display: 'inline-flex', alignItems: 'center',
              background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
              fontSize: 12, color: T.muted, textDecoration: 'none' }}>
            📋 Tablet-Ansicht
          </a>
          <a href="/admin/bookings"
            style={{ height: 34, padding: '0 14px', display: 'inline-flex', alignItems: 'center',
              background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
              fontSize: 12, color: T.muted, textDecoration: 'none' }}>
            ← Buchungen
          </a>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', height: 'calc(100vh - 52px)' }}>

        {/* LEFT: Event list */}
        <div style={{ borderRight: `1px solid ${T.border}`, overflowY: 'auto', background: T.white }}>
          {/* Stats row */}
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.borderSm}`,
            display: 'flex', gap: 12 }}>
            <StatBox label="Events" value={events.length} />
            <StatBox label="Leads total" value={totalLeads} color={T.accent} />
          </div>

          {/* Search + filters */}
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.borderSm}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Event suchen..." autoComplete="new-password"
                style={{ width: '100%', padding: '9px 12px 9px 36px', border: `1px solid ${T.border}`,
                  borderRadius: 8, fontFamily: 'inherit', fontSize: 13, background: T.bg }} />
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: T.faint, fontSize: 14 }}>🔍</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ flex: 1, padding: '7px 10px', border: `1px solid ${T.border}`, borderRadius: 7,
                  fontFamily: 'inherit', fontSize: 12, background: T.bg }}>
                <option value="">Alle Status</option>
                {['Findet statt','In Planung','Abgeschlossen'].map(s => <option key={s}>{s}</option>)}
              </select>
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                style={{ flex: 1, padding: '7px 10px', border: `1px solid ${T.border}`, borderRadius: 7,
                  fontFamily: 'inherit', fontSize: 12, background: T.bg }}>
                <option value="">Alle Typen</option>
                {['Messe','Trägerevent','Einzelveranstaltung ZBA','Präsentation'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ fontSize: 11, color: T.faint, textAlign: 'right' }}>
              {filtered.length}/{events.length} Events
            </div>
          </div>

          {/* Event cards */}
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: T.muted }}>Lade...</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: T.muted }}>Keine Events gefunden</div>
            ) : filtered.map(ev => (
              <EventCard key={ev.id} event={ev}
                selected={selected?.id === ev.id}
                onSelect={ev => setSelected(selected?.id === ev.id ? null : ev)} />
            ))}
          </div>
        </div>

        {/* RIGHT: Event detail + leads */}
        <div style={{ overflowY: 'auto', padding: 28 }}>
          {!selected ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', color: T.faint, gap: 12 }}>
              <div style={{ fontSize: 48 }}>👆</div>
              <div style={{ fontWeight: 600, fontSize: 16, color: T.muted }}>Event auswählen</div>
              <div style={{ fontSize: 13 }}>Klick auf ein Event links um die Leads zu sehen</div>
            </div>
          ) : (
            <>
              {/* Event header */}
              <div style={{ background: T.white, borderRadius: 16, padding: 24,
                border: `1px solid ${T.border}`, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>{selected.name}</h2>
                    <div style={{ color: T.muted, fontSize: 13, marginBottom: 10 }}>
                      {selected.location && <span>📍 {selected.location} · </span>}
                      {formatDate(selected.event_date)} · {daysFromNow(selected.event_date)}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Tag label={selected.status || '?'} style={STATUS_STYLE[selected.status] || { background: T.borderSm, color: T.muted }} />
                      {selected.event_type && <Tag label={selected.event_type} style={TYPE_STYLE[selected.event_type] || { background: T.borderSm, color: T.muted }} />}
                      {selected.organisator && <Tag label={`👤 ${selected.organisator}`} style={{ background: T.borderSm, color: T.muted }} />}
                    </div>
                  </div>
                  {leads.length > 0 && (
                    <div style={{ fontSize: 40, fontWeight: 900, color: T.accent, lineHeight: 1 }}>
                      {leads.length}
                      <div style={{ fontSize: 11, fontWeight: 600, color: T.muted }}>LEADS</div>
                    </div>
                  )}
                </div>

                {/* Mini stats */}
                {leads.length > 0 && (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <StatBox label="Mit Termin" value={withTermin} color={T.green} />
                    <StatBox label="Rückruf" value={ohneTermin} color={'#92400E'} />
                    <StatBox label="Bildungsangebot" value={withBGS} color={T.blue} />
                    <StatBox label="Unterschrift" value={leads.filter(l => l.signature_given).length} color={T.purple} />
                    <StatBox label="Führerschein" value={leads.filter(l => l.fuehrerschein).length} />
                  </div>
                )}
              </div>

              {/* Leads */}
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: 15, fontWeight: 700 }}>
                  {loadingLeads ? 'Lade...' : `${leads.length} Lead${leads.length !== 1 ? 's' : ''}`}
                </h3>
                {leads.length > 0 && (
                  <button onClick={() => {
                    const csv = ['Vorname,Nachname,Telefon,E-Mail,Sprachniveau,Führerschein,Termin,Kurslänge,Bildungsangebot',
                      ...leads.map(l => [l.vorname,l.nachname,l.telefon,l.email||'',l.sprachniveau||'',
                        l.fuehrerschein?'Ja':'Nein', l.termin_date?`${l.termin_date} ${l.termin_time}`:'RÜCKRUF',
                        l.kurslaenge||'', l.bildungsangebot?'Ja':'Nein'].join(','))
                    ].join('\n');
                    const a = document.createElement('a');
                    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
                    a.download = `${selected.name}-leads.csv`;
                    a.click();
                  }}
                  style={{ padding: '6px 14px', border: `1px solid ${T.border}`, borderRadius: 7,
                    background: T.white, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', color: T.muted }}>
                    ↓ CSV Export
                  </button>
                )}
              </div>

              {loadingLeads ? (
                <div style={{ textAlign: 'center', padding: 40, color: T.muted }}>Lade Leads...</div>
              ) : leads.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, background: T.white, borderRadius: 16,
                  border: `1px solid ${T.border}`, color: T.muted }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
                  <div style={{ fontWeight: 600, fontSize: 15, color: T.text, marginBottom: 4 }}>Noch keine Leads</div>
                  Öffne <strong>/events</strong> auf dem Tablet um Leads zu erfassen
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {leads.map(l => <LeadCard key={l.id} lead={l} />)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
